import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react";
import { ApiError, api, setUnauthorizedHandler } from "@/api/client";
import type { CurrentUser } from "@/types";

/** Set when a signed-in session dies mid-visit; the Login page reads it once
 *  to explain the bounce ("your session expired") instead of just appearing. */
export const SESSION_EXPIRED_KEY = "meridian:session-expired";

interface AuthState {
  user: CurrentUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  login: (email: string, password: string) => Promise<CurrentUser>;
  register: (input: {
    agency_name: string;
    email: string;
    password: string;
    full_name?: string;
  }) => Promise<CurrentUser>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await api<CurrentUser>("/api/auth/me");
      setUser(me);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Expired session → drop the cached user. The Layout guard then redirects
  // to /login (carrying the current path), so pages never sit there firing
  // doomed requests and painting error banners.
  const userRef = useRef<CurrentUser | null>(user);
  userRef.current = user;
  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (!userRef.current) return; // already signed out — nothing to do
      try {
        sessionStorage.setItem(SESSION_EXPIRED_KEY, "1");
      } catch {
        /* private mode — the notice is optional, the redirect is not */
      }
      setUser(null);
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const me = await api<CurrentUser>("/api/auth/login", {
      method: "POST",
      json: { email, password },
    });
    setUser(me);
    return me;
  }, []);

  const register = useCallback(
    async (input: {
      agency_name: string;
      email: string;
      password: string;
      full_name?: string;
    }) => {
      const me = await api<CurrentUser>("/api/auth/register", {
        method: "POST",
        json: input,
      });
      setUser(me);
      return me;
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await api("/api/auth/logout", { method: "POST" });
    } catch {
      // Server may have already expired the session; either way, clear UI.
    }
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, refresh, login, register, logout }),
    [user, loading, refresh, login, register, logout],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
